import type { FormEvent } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { FileDownloadRounded, PhotoFilterRounded } from '@mui/icons-material';
import _ from 'lodash';

import type { LatestImage } from '../../../../@types/latestImages';
import { VideaButton } from '../../../../common-components/elements/VideaButton/VideaButton';
import { isValidToothNumber } from '../../../../utils/finding';
import { useUniversalToFDI } from '../../../../utils/internationalization/useUniversalToFDI';
import { useIsWithinConfidenceThreshold } from '../../../annotator/findingSidebar/confidenceScoreFilter/helpers';
import type { RBLSide } from '../../../annotator/types';

import { FindingTable } from './styles';

export const CariesFindings = ({ image }: { image: LatestImage }) => {
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const { displayToothNumber } = useUniversalToFDI();
  if (!image.models.caries) return null;
  const findings = image.models.caries.findings;
  const distinctTeeth = _.uniqBy(findings, 'toothIdentifier');

  const findingsByTooth = distinctTeeth.map((tooth: any) => {
    const { toothIdentifier: distinctToothId } = tooth;
    const toothFindings =
      !!findings &&
      findings.filter((finding: any) => {
        const { toothIdentifier } = finding;
        if (
          !!toothIdentifier &&
          finding.toothIdentifier === distinctToothId &&
          finding.userValidation !== 'Disagree' &&
          isWithinConfidenceThreshold(
            'caries',
            finding.confidenceScore,
            finding?.bin,
          )
        ) {
          return finding;
        }
      });
    return (
      !!toothFindings && {
        distinctToothId,
        findingCount: toothFindings.length,
      }
    );
  });

  const hasNoCaries =
    !findingsByTooth ||
    findingsByTooth.every((tooth) => !!tooth && tooth.findingCount === 0);
  const findingsByToothTable =
    !!findingsByTooth &&
    findingsByTooth.length > 0 &&
    findingsByTooth?.map((findingTooth) => {
      return (
        !!findingTooth &&
        findingTooth.findingCount > 0 && (
          <tr key={`findings-${findingTooth.distinctToothId}`}>
            <td>{displayToothNumber(findingTooth.distinctToothId)}</td>
            <td>
              {`${findingTooth.findingCount} `}
              <Trans>Lesion(s)</Trans>
            </td>
          </tr>
        )
      );
    });

  return (
    !hasNoCaries && (
      <FindingTable className="finding-table">
        <thead>
          <tr>
            <th colSpan={2} className="finding-head">
              <Trans>Potential Carious Lesion Findings</Trans>
            </th>
          </tr>
          <tr>
            <th className="finding-tooth-identifier">
              <Trans>Tooth</Trans>
            </th>
            <th className="finding-tooth-value">
              <Trans>Findings</Trans>
            </th>
          </tr>
        </thead>
        <tbody>{findingsByToothTable}</tbody>
      </FindingTable>
    )
  );
};

interface RblFinding {
  sides: RBLSide[];
  toothIdentifier?: string | undefined;
}

export const RBLFindingImages = ({ image }: { image: LatestImage }) => {
  const { displayToothNumber } = useUniversalToFDI();
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const { i18n } = useLingui();
  if (!image.models.radiographic_boneloss || !image.models.calculus)
    return null;
  const rblFindings = image.models.radiographic_boneloss.findings;
  const calculusFindings = image.models.calculus.findings;
  const isPAImage = image.detectedImageType === 'PERIAPICAL';

  const rblFilteredFindings = rblFindings?.filter(
    (rblFinding) => !!rblFinding.toothIdentifier && rblFinding,
  );
  const calculusFilteredFindings = calculusFindings?.filter(
    (calculusFinding) =>
      !!calculusFinding?.toothNumber &&
      isWithinConfidenceThreshold(
        'calculus',
        calculusFinding.confidenceScore,
        calculusFinding?.bin,
      ),
  );

  const validRblFindings = rblFilteredFindings?.map((finding: RblFinding) => {
    const { toothIdentifier } = finding;
    const isValidRbl = finding.sides.some(
      (rblSide: RBLSide) => rblSide.userValidation !== 'Disagree',
    );
    const rblSet =
      !!isValidRbl &&
      finding.sides.filter(
        (rblSide: RBLSide) =>
          rblSide.side === 'mesial' || rblSide.side === 'distal',
      );
    return { rblSet, toothIdentifier };
  });

  const hasNoDisease = !validRblFindings && !calculusFilteredFindings;

  const rblFindingsByToothTable =
    !hasNoDisease &&
    validRblFindings?.map((validFinding) => {
      const { toothIdentifier, rblSet } = validFinding;
      const isValidTooth =
        !!toothIdentifier && isValidToothNumber(toothIdentifier);
      if (!isValidTooth || !rblSet) return null;
      return (
        <tr key={`findings-rbl-${toothIdentifier}`}>
          <td>{displayToothNumber(toothIdentifier)}</td>
          {rblSet.map((rblSide: RBLSide) => {
            const measure = rblSide.distanceMM;
            const unit = !isPAImage ? 'mm' : '%';
            const formattedMeasure =
              measure && !isNaN(measure)
                ? `${i18n.number(measure, {
                    style: 'decimal',
                    maximumFractionDigits: 2,
                  })}${unit}`
                : '--';
            return (
              <td
                key={`rbl-side-${rblSide.side}-${toothIdentifier}`}
                className="finding-rbl-value"
              >
                {formattedMeasure}
              </td>
            );
          })}
        </tr>
      );
    });
  const calculusFindingsByToothTable =
    !!calculusFilteredFindings &&
    calculusFilteredFindings.length > 0 &&
    calculusFilteredFindings?.map((calculusFinding) => (
      <tr key={calculusFinding.id}>
        <td>{displayToothNumber(calculusFinding?.toothNumber)}</td>
        <td className="finding-calc-value">
          <Trans>CALCULUS PRESENT</Trans>
        </td>
      </tr>
    ));
  return (
    <>
      {!hasNoDisease && !!validRblFindings && (
        <FindingTable className="finding-table">
          <thead>
            <tr>
              <th colSpan={3} className="finding-head">
                <Trans>Radiographic Bone Level Findings</Trans>
              </th>
            </tr>
            <tr>
              <th className="finding-tooth-identifier">
                <Trans>Tooth</Trans>
              </th>
              <th className="finding-tooth-value rbl-mesial">
                <Trans>Mesial</Trans>
              </th>
              <th className="finding-tooth-value rbl-distal">
                <Trans>Distal</Trans>
              </th>
            </tr>
          </thead>
          <tbody>{rblFindingsByToothTable}</tbody>
        </FindingTable>
      )}
      {!hasNoDisease &&
        !!calculusFilteredFindings &&
        calculusFilteredFindings.length > 0 && (
          <FindingTable className="finding-table">
            <thead>
              <tr>
                <th colSpan={2} className="finding-head">
                  <Trans>Potential Calculus Findings</Trans>
                </th>
              </tr>
              <tr>
                <th className="finding-tooth-identifier">
                  <Trans>Tooth</Trans>
                </th>
                <th className="finding-tooth-value calculus">
                  <Trans>Finding</Trans>
                </th>
              </tr>
            </thead>
            <tbody>{calculusFindingsByToothTable}</tbody>
          </FindingTable>
        )}
    </>
  );
};

export const EndoFindingImages = ({ image }: { image: LatestImage }) => {
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const { displayToothNumber } = useUniversalToFDI();
  if (!image.models.periapical_radiolucency) return null;
  const findings = image.models.periapical_radiolucency.findings;

  const filteredFindings =
    !!findings &&
    findings.filter(
      (finding) =>
        !!finding.toothNumber &&
        finding &&
        finding.userValidation !== 'Disagree' &&
        isWithinConfidenceThreshold(
          'periapical_radiolucency',
          finding.confidenceScore,
          finding?.bin,
        ),
    );

  const findingsByToothTable =
    !!filteredFindings &&
    filteredFindings.length > 0 &&
    filteredFindings?.map(
      (validFinding, index) =>
        !!validFinding && (
          <tr key={`findings-endo-${index}`}>
            <td>{displayToothNumber(validFinding.toothNumber)}</td>

            <td className="finding-rbl-value">
              <Trans>Periapical Radiolucency Present</Trans>
            </td>
          </tr>
        ),
    );

  return (
    filteredFindings.length > 0 && (
      <FindingTable className="finding-table">
        <thead>
          <tr>
            <th colSpan={2} className="finding-head">
              <Trans>Potential Endodontic Findings</Trans>
            </th>
          </tr>
          <tr>
            <th className="finding-tooth-identifier">
              <Trans>Tooth</Trans>
            </th>
            <th className="finding-tooth-value">
              <Trans>Findings</Trans>
            </th>
          </tr>
        </thead>
        <tbody>{findingsByToothTable}</tbody>
      </FindingTable>
    )
  );
};

export const DownloadJpgButton = ({
  props: { handleExport },
}: {
  props: { handleExport: (e: FormEvent, type: string) => void };
}) => (
  <VideaButton
    onClick={(e: FormEvent<HTMLButtonElement>) => handleExport(e, 'jpg')}
  >
    <PhotoFilterRounded /> Save Jpeg Image
  </VideaButton>
);

export const DownloadPdfButton = ({
  props: { handleExport },
}: {
  props: { handleExport: (e: FormEvent, type: string) => void };
}) => (
  <VideaButton
    onClick={(e: FormEvent<HTMLButtonElement>) => handleExport(e, 'pdf')}
  >
    <FileDownloadRounded /> Save PDF
  </VideaButton>
);
